import React, { useContext, useMemo, useState, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import MenuItemsPageContext, { IMenuItemContext } from '../MenuItemsPageContext/MenuItemsPageContext';
import COUNTRY_LOCALES_MAP, { DEFAULT_LOCALE } from '../../constants/localeConstants';
import Spinner from '../../components/spinner/Spinner';

// Dynamically load messages for a given locale
async function loadMessages(locale: string): Promise<Record<string, string>> {
	try {
		const messages = await import(`../../translations/${locale}`);
		return messages.default;
	} catch (error) {
		const fallback = await import('../../translations/en-US');
		return fallback.default;
	}
}

interface IntlProviderWrapperProps {
	children: React.ReactNode;
}

export const IntlProviderWrapper: React.FC<IntlProviderWrapperProps> = ({ children }) => {
	const menuContext = useContext(MenuItemsPageContext) as IMenuItemContext;
	const merchantData = menuContext?.merchantData;
	const [messages, setMessages] = useState<Record<string, string> | null>(null);

	// Determine the locale based on merchant's country code
	const locale = useMemo(() => {
		if (!merchantData?.countryCode) {
			return DEFAULT_LOCALE;
		}

		// Get the locale(s) for the country from the COUNTRY_LOCALES_MAP
		const countryLocales = COUNTRY_LOCALES_MAP[merchantData.countryCode];

		if (!countryLocales || countryLocales.length === 0) {
			return DEFAULT_LOCALE;
		}

		// Use the first locale for the country (primary locale)
		const [primaryLocale] = countryLocales;

		return primaryLocale;
	}, [merchantData?.countryCode]);

	// Load messages dynamically when locale changes
	useEffect(() => {
		loadMessages(locale).then(setMessages);
	}, [locale]);

	// Don't render until messages are loaded
	if (!messages) {
		return <Spinner />; // or a skeleton screen
	}

	return (
		<IntlProvider locale={locale} messages={messages} defaultLocale={DEFAULT_LOCALE}>
			{children}
		</IntlProvider>
	);
};

export default IntlProviderWrapper;
